import { DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Flex, Input, InputNumber, Select } from "antd";
import queryClient from "api/queryClient.ts";
import { useStoreCommissionMutation, useUpdateCommissionMutation } from "api/TenantSlug/Commission/hooks";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { CascadeSelectSearchable } from "components/UI/Selects/CascadeSelectSearchable/CascadeSelectSearchable.tsx";
import { ConversionTypeSelectSearchable } from "components/UI/Selects/ConversionTypeSelectSearchable/ConversionTypeSelectSearchable.tsx";
import { OfferSelectSearchable } from "components/UI/Selects/OfferSelectSearchable/OfferSelectSearchable.tsx";
import { SourceSelectSearchable } from "components/UI/Selects/SourceSelectSearchable/SourceSelectSearchable.tsx";
import { TransactionTypeSelectSearchable } from "components/UI/Selects/TransactionTypeSelectSearchable/TransactionTypeSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { AutoResultInterval, CommissionType, FormulaType, PayoutResult } from "types/enums.ts";
import { FormType } from "types/models.ts";
import {
    createFormulaParameterCategory,
    PAYOUT_FORMULA_PLACEHOLDER,
} from "utils/commission/formulaParameterCategories";

import { CommissionFormData, schema } from "./formSchema";
import { getManageCommissionFormKey } from "./getManageCommissionFormKey";

import CommissionData = App.Data.CommissionData;
import StoreCommissionRequest = App.Http.Requests.Commission.StoreCommissionRequest;
import UpdateCommissionRequest = App.Http.Requests.Commission.UpdateCommissionRequest;

const PAYOUT_AMOUNT_PLACEHOLDER = "10";

const getRecurrenceTypeFromValue = (recurrence: number | null | undefined): "once" | "limited" | "unlimited" => {
    if (recurrence === null || recurrence === undefined) {
        return "unlimited";
    }
    if (recurrence === 1) {
        return "once";
    }
    return "limited";
};

interface DetailCommissionContentProps {
    disableFields?: boolean;
    isLimitedChangeEnabled?: boolean;
    isAddButtonEnabled?: boolean;
    commission?: CommissionData;
    /** When set, the drawer was opened from within that offer's own context — lock the Offer
     * field to it instead of letting the user pick a different offer. */
    offerId?: string;
}

export const ManageCommissionFormContent: FC<DetailCommissionContentProps> = ({
    disableFields = false,
    isLimitedChangeEnabled = false,
    isAddButtonEnabled = false,
    commission,
    offerId,
}) => {
    const { t: tF } = usePaldockTranslation("forms.manage-commission");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tG } = usePaldockTranslation("general");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const generalFormUtils = useGeneralForm<CommissionFormData>();
    const { control, fieldNames, setValue, reset, getValues } = generalFormUtils;

    const formulaTypeOptions = [
        { label: tF("options.formula_type.fix"), value: FormulaType.Fix },
        { label: tF("options.formula_type.formula"), value: FormulaType.Formula },
        { label: tF("options.formula_type.percent"), value: FormulaType.Percent },
        { label: tG("status.disabled"), value: "disabled" },
    ];

    const selectedOfferId = useWatch({
        control,
        name: fieldNames.offer_id.name,
    });

    const recurrenceTypeValue = useWatch({
        control,
        name: fieldNames.recurrence_type?.name || "recurrence_type",
    });

    const recurrenceTypeOptions = [
        { label: tF("options.recurrence.once"), value: "once" },
        { label: tF("options.recurrence.limited"), value: "limited" },
        { label: tF("options.recurrence.unlimited"), value: "unlimited" },
    ];

    const isRecurrenceLimited = recurrenceTypeValue === "limited";
    const showRecurrenceWarning = recurrenceTypeValue !== "once";

    const autoresultIntervalValue = useWatch({
        control,
        name: fieldNames.autoresult_interval?.name || "autoresult_interval",
    });

    const autoresultIntervalOptions = [
        { label: tF("options.autoresult_interval.disabled"), value: null },
        { label: tF("options.autoresult_interval.seconds"), value: AutoResultInterval.Seconds },
        { label: tF("options.autoresult_interval.minutes"), value: AutoResultInterval.Minutes },
        { label: tF("options.autoresult_interval.hours"), value: AutoResultInterval.Hours },
        { label: tF("options.autoresult_interval.days"), value: AutoResultInterval.Days },
    ];

    const autoresultResultOptions = [
        { label: tG("payout_result.approved"), value: PayoutResult.Approved },
        { label: tG("payout_result.pending"), value: PayoutResult.Pending },
        { label: tG("payout_result.rejected"), value: PayoutResult.Rejected },
    ];

    const isAutoresultDisabled = autoresultIntervalValue === null;

    const formulaTypeValue = useWatch({
        control,
        name: fieldNames.formula_type?.name || "formula_type",
    });

    const isFormulaDisabled = formulaTypeValue === "disabled";

    const formulaCategories = useMemo(() => [createFormulaParameterCategory(tF)], [tF]);

    // Clear the value when entering or leaving Formula (it holds a tag expression, not a plain
    // number/percent), and set a default value when switching to disabled.
    const previousFormulaTypeRef = useRef(formulaTypeValue);
    useEffect(() => {
        const payoutFormulaName = fieldNames.payout_formula?.name || "payout_formula";
        const crossingFormulaBoundary =
            (previousFormulaTypeRef.current === FormulaType.Formula) !== (formulaTypeValue === FormulaType.Formula);
        if (crossingFormulaBoundary) {
            setValue(payoutFormulaName, "");
        }
        if (formulaTypeValue === "disabled") {
            const currentValue = getValues(payoutFormulaName);
            if (!currentValue || currentValue.trim() === "") {
                setValue(payoutFormulaName, "0");
            }
        }
        previousFormulaTypeRef.current = formulaTypeValue;
    }, [formulaTypeValue, setValue, getValues, fieldNames.payout_formula?.name]);

    // Reset form when commission changes (after refetch)
    useEffect(() => {
        if (commission && commission.id) {
            const resetValues =
                commission.commission_type === CommissionType.User
                    ? {
                          ...commission,
                          user_id: commission.parent?.user_id,
                          recurrence: commission.parent?.recurrence,
                          recurrence_type: getRecurrenceTypeFromValue(commission.parent?.recurrence),
                          autoresult_interval: commission.parent?.autoresult_interval,
                          autoresult_value: commission.parent?.autoresult_value,
                          autoresult_result: commission.parent?.autoresult_result,
                          // Show "disabled" in UI if commission is disabled
                          formula_type:
                              commission.is_enabled === false ? ("disabled" as const) : commission.formula_type,
                      }
                    : {
                          ...commission,
                          recurrence_type: getRecurrenceTypeFromValue(commission.recurrence),
                          // Show "disabled" in UI if commission is disabled
                          formula_type:
                              commission.is_enabled === false ? ("disabled" as const) : commission.formula_type,
                      };
            // Sync before reset so the formula-boundary effect above doesn't mistake this refetch
            // (previous ref stale from before the reset) for a user-driven type change and wipe a
            // formula value that was just freshly fetched from the backend.
            previousFormulaTypeRef.current = resetValues.formula_type;
            reset(resetValues);
        }
    }, [commission, reset]);

    useEffect(() => {
        if (commission?.offer_id && selectedOfferId === commission.offer_id) {
            return;
        }
        if (fieldNames.cascade_id) {
            setValue(fieldNames.cascade_id.name, null);
        }
    }, [selectedOfferId, setValue, fieldNames.cascade_id?.name]);

    return (
        <>
            <DrawerCollapsible title={tF("sections.conditions.title")}>
                <div className="grid rounded-lg grid-cols-3 gap-x-4 col-span-3">
                    <FormItem control={control} name={fieldNames.offer_id.name} label={tFields("offer.label")} required>
                        <OfferSelectSearchable
                            disabled={
                                disableFields ||
                                !!offerId ||
                                (commission && commission.commission_type === CommissionType.User)
                            }
                        />
                    </FormItem>
                    {(!commission || commission.commission_type === CommissionType.Tenant) && (
                        <FormItem
                            control={control}
                            name={fieldNames.user_id?.name || "user_id"}
                            label={tFields("advertiser.label")}
                        >
                            <AdvertiserSelectSearchable disabled={disableFields} allowClear />
                        </FormItem>
                    )}
                    {commission && commission.commission_type === CommissionType.User && (
                        <FormItem
                            control={control}
                            name={fieldNames.user_id?.name || "user_id"}
                            label={tFields("advertiser.label")}
                        >
                            <AdvertiserSelectSearchable disabled={true} allowClear />
                        </FormItem>
                    )}
                    <FormItem
                        control={control}
                        name={fieldNames.conversion_type?.name || "conversion_type"}
                        label={tFields("conversion_type.label")}
                        required
                    >
                        <ConversionTypeSelectSearchable
                            disabled={
                                disableFields || (commission && commission.commission_type === CommissionType.User)
                            }
                        />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.conversion_source?.name || "conversion_source"}
                        label={tFields("source.label")}
                    >
                        <SourceSelectSearchable
                            allowClear
                            disabled={
                                disableFields || (commission && commission.commission_type === CommissionType.User)
                            }
                        />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.cascade_id?.name || "cascade_id"}
                        label={tFields("channel.label")}
                    >
                        <CascadeSelectSearchable
                            offerId={selectedOfferId}
                            disabled={
                                disableFields || (commission && commission.commission_type === CommissionType.User)
                            }
                            allowClear
                        />
                    </FormItem>
                </div>
            </DrawerCollapsible>
            <DrawerCollapsible title={tC("settings")}>
                <div className="grid rounded-lg grid-cols-3 gap-x-4 col-span-3">
                    <FormItem
                        control={control}
                        name={fieldNames.is_autoapprove?.name || "is_autoapprove"}
                        label={tF("fields.is_autoapprove.label")}
                        disabled={disableFields || (commission && commission.commission_type === CommissionType.User)}
                    >
                        <Select
                            placeholder={tF("fields.is_autoapprove.placeholder")}
                            options={[
                                {
                                    label: tC("yes"),
                                    value: true,
                                },
                                {
                                    label: tC("no"),
                                    value: false,
                                },
                            ]}
                            disabled={disableFields}
                        />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.autocreate_conversion_type?.name || "autocreate_conversion_type"}
                        label={tF("fields.autocreate_conversion_type.label")}
                        disabled={disableFields || (commission && commission.commission_type === CommissionType.User)}
                    >
                        <TransactionTypeSelectSearchable allowClear={true} />
                    </FormItem>
                </div>
                <div className="grid rounded-lg grid-cols-3 gap-x-4 col-span-3">
                    <FormItem
                        control={control}
                        name={fieldNames.recurrence_type?.name || "recurrence_type"}
                        label={tF("fields.recurrence_type.label")}
                    >
                        <Select
                            placeholder={tF("fields.recurrence_type.placeholder")}
                            options={recurrenceTypeOptions}
                            disabled={
                                disableFields || (commission && commission.commission_type === CommissionType.User)
                            }
                            value={recurrenceTypeValue}
                            onChange={(value) => {
                                if (value === "once") {
                                    setValue(fieldNames.recurrence?.name || "recurrence", 1);
                                } else if (value === "unlimited") {
                                    setValue(fieldNames.recurrence?.name || "recurrence", null);
                                } else {
                                    setValue(fieldNames.recurrence?.name || "recurrence", 2);
                                }
                            }}
                        />
                    </FormItem>
                    {isRecurrenceLimited && (
                        <FormItem
                            control={control}
                            name={fieldNames.recurrence?.name || "recurrence"}
                            label={tF("fields.recurrence.label")}
                        >
                            <InputNumber
                                placeholder={"2"}
                                min={2}
                                disabled={
                                    disableFields || (commission && commission.commission_type === CommissionType.User)
                                }
                                className="w-full"
                            />
                        </FormItem>
                    )}
                </div>
                {showRecurrenceWarning && (
                    <div className="col-span-3">
                        <Alert
                            message={tF("warnings.recurrence_postback")}
                            type="warning"
                            icon={<ExclamationCircleOutlined />}
                            showIcon
                            className="mb-4"
                        />
                    </div>
                )}
                <div className="grid rounded-lg grid-cols-3 gap-x-4 col-span-3">
                    <FormItem
                        control={control}
                        name={fieldNames.autoresult_interval?.name || "autoresult_interval"}
                        label={tF("fields.autoresult_interval.label")}
                    >
                        <Select
                            placeholder={tF("fields.autoresult_interval.placeholder")}
                            options={autoresultIntervalOptions}
                            disabled={
                                disableFields || (commission && commission.commission_type === CommissionType.User)
                            }
                            value={autoresultIntervalValue}
                            onChange={(value) => {
                                setValue(fieldNames.autoresult_interval?.name || "autoresult_interval", value);
                                if (value === null) {
                                    setValue(fieldNames.autoresult_value?.name || "autoresult_value", 0);
                                    setValue(
                                        fieldNames.autoresult_result?.name || "autoresult_result",
                                        PayoutResult.Pending
                                    );
                                } else {
                                    // When enabling, ensure we have valid default values
                                    const currentValue = getValues(
                                        fieldNames.autoresult_value?.name || "autoresult_value"
                                    );
                                    const currentResult = getValues(
                                        fieldNames.autoresult_result?.name || "autoresult_result"
                                    );
                                    if (!currentValue)
                                        setValue(fieldNames.autoresult_value?.name || "autoresult_value", 0);
                                    if (!currentResult)
                                        setValue(
                                            fieldNames.autoresult_result?.name || "autoresult_result",
                                            PayoutResult.Pending
                                        );
                                }
                            }}
                        />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.autoresult_value?.name || "autoresult_value"}
                        label={tF("fields.autoresult_value.label")}
                    >
                        <InputNumber
                            placeholder={tF("fields.autoresult_value.placeholder")}
                            min={0}
                            disabled={
                                disableFields ||
                                isAutoresultDisabled ||
                                (commission && commission.commission_type === CommissionType.User)
                            }
                            className="w-full"
                        />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.autoresult_result?.name || "autoresult_result"}
                        label={tF("fields.autoresult_result.label")}
                    >
                        <Select
                            placeholder={tF("fields.autoresult_result.placeholder")}
                            options={autoresultResultOptions}
                            disabled={
                                disableFields ||
                                isAutoresultDisabled ||
                                (commission && commission.commission_type === CommissionType.User)
                            }
                        />
                    </FormItem>
                </div>
                <div className="grid rounded-lg grid-cols-3 gap-x-4 col-span-3">
                    <FormItem
                        control={control}
                        name={fieldNames.payout_formula?.name || "payout_formula"}
                        label={tF("fields.payout_formula.label")}
                        required
                    >
                        {formulaTypeValue === FormulaType.Formula ? (
                            <TagTextInput
                                categories={formulaCategories}
                                mode="free"
                                placeholder={PAYOUT_FORMULA_PLACEHOLDER}
                                disabled={disableFields || isFormulaDisabled}
                            />
                        ) : (
                            <Input
                                placeholder={PAYOUT_AMOUNT_PLACEHOLDER}
                                disabled={disableFields || isFormulaDisabled}
                            />
                        )}
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.formula_type?.name || "formula_type"}
                        label={tF("fields.formula_type.label")}
                        required
                    >
                        <Select
                            placeholder={tF("fields.formula_type.placeholder")}
                            options={formulaTypeOptions}
                            disabled={disableFields}
                        />
                    </FormItem>
                    <FormItem
                        control={control}
                        name={fieldNames.payout_placeholder?.name || "payout_placeholder"}
                        label={tF("fields.payout_placeholder.label")}
                    >
                        <Input placeholder={tF("fields.payout_placeholder.placeholder")} disabled={disableFields} />
                    </FormItem>
                </div>
                <div className="grid rounded-lg grid-cols-3 gap-x-4 col-span-3">
                    <FormItem
                        control={control}
                        name={fieldNames.description?.name || "description"}
                        label={tFields("description.label")}
                        className={"col-span-3"}
                    >
                        <Input placeholder={tFields("description.placeholder")} disabled={disableFields} />
                    </FormItem>
                </div>
            </DrawerCollapsible>
            {isLimitedChangeEnabled && (
                <DrawerCollapsible
                    title={tF("sections.limitedChange.title")}
                    extra={<Button type="primary" icon={<DeleteOutlined />} />}
                >
                    <div className="grid rounded-lg grid-cols-2 gap-x-4 ">
                        <FormItem
                            control={control}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            name={fieldNames.enabled_from?.name || "enabled_from"}
                            label={tC("dates.from")}
                        >
                            <DatePicker className={"w-full"} />
                        </FormItem>
                        <FormItem
                            control={control}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            name={fieldNames.enabled_to?.name || "enabled_to"}
                            label={tC("dates.to")}
                        >
                            <DatePicker className={"w-full"} />
                        </FormItem>
                    </div>
                    <div className="grid rounded-lg grid-cols-3 gap-x-4 ">
                        <FormItem
                            control={control}
                            name={fieldNames.payout_formula?.name || "payout_formula"}
                            label={tF("fields.payout_formula.label")}
                            className={"col-span-2"}
                        >
                            {formulaTypeValue === FormulaType.Formula ? (
                                <TagTextInput
                                    categories={formulaCategories}
                                    mode="free"
                                    placeholder={PAYOUT_FORMULA_PLACEHOLDER}
                                />
                            ) : (
                                <Input placeholder={PAYOUT_AMOUNT_PLACEHOLDER} />
                            )}
                        </FormItem>
                        <FormItem
                            control={control}
                            name={fieldNames.payout_placeholder?.name || "payout_placeholder"}
                            label={tF("fields.payout_placeholder.label")}
                        >
                            <Input />
                        </FormItem>
                    </div>
                </DrawerCollapsible>
            )}
            {isAddButtonEnabled && (
                <div className="rounded-lg cursor-pointer bg-white m-3 p-12">
                    <Flex justify={"center"} align={"center"} gap={"middle"} className={"text-xlg"}>
                        <PlusOutlined /> {tC("actions.add")}
                    </Flex>
                </div>
            )}
        </>
    );
};

interface ManageCommissionFormProps {
    formId: string;
    commission?: CommissionData;
    offerId?: string;
    onSubmit?: () => void;
    formType: FormType;
}

export const ManageCommissionForm: FC<ManageCommissionFormProps> = ({
    formId,
    commission,
    offerId,
    onSubmit,
    formType,
}) => {
    const generalFormUtils = useGeneralForm<CommissionFormData>();
    const storeCommissionMutation = useStoreCommissionMutation();
    const updateCommissionMutation = useUpdateCommissionMutation();
    const { t: tF } = usePaldockTranslation("forms.manage-commission");

    let defaultValues;
    if (formType === FormType.Create) {
        defaultValues = {
            offer_id: offerId ?? "",
            user_id: null,
            conversion_type: "",
            conversion_source: null,
            is_autoapprove: false,
            autocreate_conversion_type: null,
            formula_type: FormulaType.Fix,
            payout_formula: "",
            payout_placeholder: null,
            description: null,
            cascade_id: null,
            recurrence: 1,
            recurrence_type: "once" as const,
            autoresult_interval: null,
            autoresult_value: 0,
            autoresult_result: PayoutResult.Pending,
        };
    } else if (formType === FormType.Detail || formType === FormType.Edit) {
        if (commission && commission.id) {
            if (commission.commission_type === CommissionType.User) {
                defaultValues = {
                    ...commission,
                    user_id: commission.parent?.user_id,
                    recurrence: commission.parent?.recurrence,
                    recurrence_type: getRecurrenceTypeFromValue(commission.parent?.recurrence),
                };
            } else if (commission.commission_type === CommissionType.Tenant) {
                defaultValues = {
                    ...commission,
                    recurrence_type: getRecurrenceTypeFromValue(commission.recurrence),
                };
            } else {
                defaultValues = {
                    ...commission,
                    recurrence_type: getRecurrenceTypeFromValue(commission.recurrence),
                };
            }
        } else {
            return <>{tF("messages.missing_commission")}</>;
        }
    }

    if ((formType === FormType.Detail || formType === FormType.Edit) && (!commission || !commission.id)) {
        return <>{tF("messages.missing_commission")}</>;
    }

    const handleFormSubmit = (values: CommissionFormData) => {
        if (formType === FormType.Create) {
            // Handle disabled state mapping
            const isDisabled = values.formula_type === "disabled";
            const actualFormulaType = values.formula_type === "disabled" ? FormulaType.Fix : values.formula_type;

            const newCommission: StoreCommissionRequest = {
                offer_id: values.offer_id,
                user_id: values.user_id ?? null,
                conversion_type: values.conversion_type,
                conversion_source: values.conversion_source ?? null,
                is_enabled: !isDisabled,
                is_autoapprove: values.is_autoapprove,
                autocreate_conversion_type: values.autocreate_conversion_type ?? null,
                formula_type: actualFormulaType,
                payout_formula: values.payout_formula,
                payout_placeholder: values.payout_placeholder,
                description: values.description,
                commission_type: CommissionType.Tenant,
                parent_id: null,
                commission_group_id: null,
                enabled_from: null,
                enabled_to: null,
                conversion_status: null,
                advertiser_id: null,
                integration_id: null,
                cascade_id: values.cascade_id ?? null,
                recurrence: values.recurrence ?? null,
                autoresult_interval: values.autoresult_interval,
                autoresult_value: values.autoresult_value,
                autoresult_result: values.autoresult_result,
            };
            storeCommissionMutation.mutate(newCommission, {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["getCommissions"] });
                    toast.success(tF("messages.add_success"));
                    onSubmit?.();
                },
                onError: (error) => {
                    generalFormUtils?.handleErrorsFromErrorResponse(error);
                    console.error(`Error storing commission: ${(error as Error)?.message ?? "Unknown error"}`);
                },
            });
        } else if (formType === FormType.Edit) {
            if (commission && commission.id) {
                let updateCommission: UpdateCommissionRequest = {};
                // Handle disabled state mapping
                const isDisabled = values.formula_type === "disabled";
                const actualFormulaType: FormulaType =
                    values.formula_type !== "disabled" ? values.formula_type : commission.formula_type;

                if (commission.commission_type === CommissionType.Tenant) {
                    updateCommission = {
                        ...updateCommission,
                        ...{
                            offer_id: values.offer_id,
                            cascade_id: values.cascade_id ?? null,
                            user_id: values.user_id ?? null,
                            conversion_type: values.conversion_type,
                            conversion_source: values.conversion_source ?? null,
                            is_enabled: !isDisabled,
                            is_autoapprove: values.is_autoapprove,
                            autocreate_conversion_type: values.autocreate_conversion_type ?? null,
                            formula_type: actualFormulaType,
                            payout_formula: values.payout_formula,
                            payout_placeholder: values.payout_placeholder ?? null,
                            description: values.description ?? null,
                            recurrence: values.recurrence ?? null,
                            autoresult_interval: values.autoresult_interval,
                            autoresult_value: values.autoresult_value,
                            autoresult_result: values.autoresult_result,
                        },
                    };
                } else if (commission.commission_type === CommissionType.User) {
                    updateCommission = {
                        ...updateCommission,
                        ...{
                            is_enabled: !isDisabled,
                            payout_formula: values.payout_formula,
                            payout_placeholder: values.payout_placeholder ?? null,
                            formula_type: actualFormulaType,
                            description: values.description ?? null,
                        },
                    };
                }
                updateCommissionMutation.mutate(
                    { commissionId: commission.id, request: updateCommission },
                    {
                        onSuccess: () => {
                            toast.success(tF("messages.update_success"));
                            onSubmit?.();
                        },
                        onError: (error) => {
                            generalFormUtils?.handleErrorsFromErrorResponse(error);
                            toast.error(tF("messages.update_error"));
                        },
                    }
                );
            }
        }
    };

    return (
        <GeneralFormProvider
            // GeneralFormProvider's useForm only reads defaultValues on mount, and
            // resetOnSubmit's reset() falls back to that same stale snapshot — without this key,
            // a form left mounted while its context changes (switching which commission is being
            // edited, or which offer a new commission is being created under) would keep showing
            // the previous entity's data instead of remounting with fresh defaultValues.
            key={getManageCommissionFormKey(commission?.id, offerId)}
            schema={schema}
            defaultValues={defaultValues}
            id={formId}
            onSubmit={handleFormSubmit}
            resetOnSubmit={formType === FormType.Create}
        >
            <ManageCommissionFormContent
                disableFields={formType === FormType.Detail}
                commission={commission}
                offerId={formType === FormType.Create ? offerId : undefined}
            />
        </GeneralFormProvider>
    );
};
